/**
 * Pure logic for the three conversations that hang off a catchball loop:
 * inquiries (handoff_inquiries), revision requests (revision_requests) and
 * level involvements (catchball_level_involvements).
 *
 * Authority lives on the server (migrations 713 / 718). Nothing here grants
 * anything — it only decides what the UI offers and, when it does not offer
 * something, the reason it shows. A control is disabled only when the client
 * is CERTAIN the server would refuse; anything it cannot know (tier / org-unit
 * eligibility) is left enabled and the server's own refusal text is shown.
 */

import type { CatchballTierInvolvement } from '../../../../types/prioritization/catchballTierTurn';

// ── Rows ─────────────────────────────────────────────────────────────────────

export interface LoopInquiry {
  id: string;
  loopId: string;
  question: string;
  status: string;
  openedBy: string;
  openedAt: string;
  answer: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
}

export interface LoopRevisionRequest {
  id: string;
  loopId: string;
  message: string;
  reasonCode: string | null;
  status: string;
  requestedBy: string;
  requestedAt: string;
  closedBy: string | null;
  closedAt: string | null;
}

/** The loop facts the server's authority predicates read that the client also holds. */
export interface LoopPartyFacts {
  ballHolderUserId?: string | null;
  createdBy?: string | null;
  requestIssuedBy?: string | null;
  /** Cycle owner, or a role that edits the cycle (workspace admin/owner). */
  isCycleOwnerOrAdmin?: boolean;
}

export interface ActionGate {
  allowed: boolean;
  /** Shown next to / as the title of the disabled control. */
  reason?: string;
}

const ALLOWED: ActionGate = { allowed: true };
const SIGN_IN: ActionGate = { allowed: false, reason: 'Sign in to do this.' };

// ── Status ───────────────────────────────────────────────────────────────────

/**
 * catchball_open_inquiry / catchball_create_revision_request write 'OPEN' in
 * caps, but nothing constrains the column — compare case-insensitively so a
 * real row never loses its controls over spelling.
 */
export function isOpenStatus(status: string | null | undefined): boolean {
  return (status ?? '').trim().toUpperCase() === 'OPEN';
}

export type InquiryDisplayStatus = 'open' | 'answered' | 'withdrawn' | 'other';

export function inquiryDisplayStatus(inquiry: Pick<LoopInquiry, 'status'>): InquiryDisplayStatus {
  const s = (inquiry.status ?? '').trim().toUpperCase();
  if (s === 'OPEN') return 'open';
  if (s === 'ANSWERED') return 'answered';
  if (s === 'CANCELLED' || s === 'CANCELED') return 'withdrawn';
  return 'other';
}

export const INQUIRY_STATUS_LABELS: Record<InquiryDisplayStatus, string> = {
  open: 'Open',
  answered: 'Answered',
  withdrawn: 'Withdrawn',
  other: 'Closed',
};

export const REVISION_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  CLOSED: 'Addressed',
  WITHDRAWN: 'Withdrawn',
  SUPERSEDED: 'Superseded',
};

export function revisionStatusLabel(status: string | null | undefined): string {
  const key = (status ?? '').trim().toUpperCase();
  return REVISION_STATUS_LABELS[key] ?? (status || 'Unknown');
}

// ── Sorting ──────────────────────────────────────────────────────────────────

function time(value: string | null | undefined): number {
  if (!value) return 0;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Open first; among open ones, questions someone else asked (the ones the
 * viewer can answer) before the viewer's own; oldest waiting first. Closed
 * threads follow, most recently opened first.
 */
export function sortInquiries(inquiries: LoopInquiry[], viewerId: string | null | undefined): LoopInquiry[] {
  return [...inquiries].sort((a, b) => {
    const aOpen = isOpenStatus(a.status);
    const bOpen = isOpenStatus(b.status);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    if (aOpen) {
      const aMine = Boolean(viewerId) && a.openedBy === viewerId;
      const bMine = Boolean(viewerId) && b.openedBy === viewerId;
      if (aMine !== bMine) return aMine ? 1 : -1;
      return time(a.openedAt) - time(b.openedAt);
    }
    return time(b.openedAt) - time(a.openedAt);
  });
}

/** Open first (oldest waiting first), then closed, most recently requested first. */
export function sortRevisionRequests(requests: LoopRevisionRequest[]): LoopRevisionRequest[] {
  return [...requests].sort((a, b) => {
    const aOpen = isOpenStatus(a.status);
    const bOpen = isOpenStatus(b.status);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    return aOpen
      ? time(a.requestedAt) - time(b.requestedAt)
      : time(b.requestedAt) - time(a.requestedAt);
  });
}

export function countOpen(rows: Array<{ status: string }>): number {
  return rows.filter((r) => isOpenStatus(r.status)).length;
}

// ── Who is a party to the loop ───────────────────────────────────────────────

/**
 * The part of internal_catchball_loop_actor_permitted (718) the client can
 * evaluate: ball holder, loop creator, requester, cycle owner / admin. The
 * server additionally admits tier- or org-unit-eligible workspace members,
 * which the client cannot see — so `false` here means "not known to be a
 * party", not "refused".
 */
export function isKnownLoopParty(viewerId: string | null | undefined, facts: LoopPartyFacts): boolean {
  if (!viewerId) return false;
  return (
    facts.ballHolderUserId === viewerId ||
    facts.createdBy === viewerId ||
    facts.requestIssuedBy === viewerId ||
    Boolean(facts.isCycleOwnerOrAdmin)
  );
}

// ── Inquiries ────────────────────────────────────────────────────────────────

/** Anyone who can hold or see the ball may ask; the server applies the floor. */
export function canAskQuestion(viewerId: string | null | undefined): ActionGate {
  return viewerId ? ALLOWED : SIGN_IN;
}

/**
 * The inquiry has no addressee column — it is asked of the loop. The party
 * being asked is therefore everyone permitted on the loop EXCEPT the asker:
 * you cannot answer your own question.
 */
export function canAnswerInquiry(inquiry: LoopInquiry, viewerId: string | null | undefined): ActionGate {
  if (!viewerId) return SIGN_IN;
  if (!isOpenStatus(inquiry.status)) {
    return { allowed: false, reason: `This question is ${INQUIRY_STATUS_LABELS[inquiryDisplayStatus(inquiry)].toLowerCase()}.` };
  }
  if (inquiry.openedBy === viewerId) {
    return { allowed: false, reason: 'You asked this — someone else on the loop answers it.' };
  }
  return ALLOWED;
}

export function canWithdrawInquiry(inquiry: LoopInquiry, viewerId: string | null | undefined): ActionGate {
  if (!viewerId) return SIGN_IN;
  if (!isOpenStatus(inquiry.status)) {
    return { allowed: false, reason: `This question is ${INQUIRY_STATUS_LABELS[inquiryDisplayStatus(inquiry)].toLowerCase()}.` };
  }
  if (inquiry.openedBy !== viewerId) {
    return { allowed: false, reason: 'Only the person who asked can withdraw a question.' };
  }
  return ALLOWED;
}

/**
 * Open questions that are waiting on the viewer: asked by someone else, on a
 * loop the viewer is a known party to. Oldest first — longest-waiting on top.
 */
export function inquiriesAwaitingViewer(
  inquiries: LoopInquiry[],
  viewerId: string | null | undefined,
  factsByLoop: Record<string, LoopPartyFacts | undefined>,
): LoopInquiry[] {
  if (!viewerId) return [];
  return inquiries
    .filter((q) => isOpenStatus(q.status) && q.openedBy !== viewerId)
    .filter((q) => {
      const facts = factsByLoop[q.loopId];
      return facts ? isKnownLoopParty(viewerId, facts) : false;
    })
    .sort((a, b) => time(a.openedAt) - time(b.openedAt));
}

// ── Revision requests ────────────────────────────────────────────────────────

const REVISION_AUTHORITY =
  'the ball holder, the loop creator or requester, the cycle owner, or a workspace admin/owner';

/**
 * catchball_create_revision_request (713) admits exactly the known loop
 * parties plus super admin — no tier-eligibility branch — so the client can
 * gate this one with confidence.
 */
export function canRequestRevision(viewerId: string | null | undefined, facts: LoopPartyFacts): ActionGate {
  if (!viewerId) return SIGN_IN;
  if (!isKnownLoopParty(viewerId, facts)) {
    return { allowed: false, reason: `Requesting a revision requires ${REVISION_AUTHORITY}.` };
  }
  return ALLOWED;
}

/** Same authority as creating one (713), and the request must still be open. */
export function canCloseRevisionRequest(
  request: LoopRevisionRequest,
  viewerId: string | null | undefined,
  facts: LoopPartyFacts,
): ActionGate {
  if (!viewerId) return SIGN_IN;
  if (!isOpenStatus(request.status)) {
    return { allowed: false, reason: `This request is already ${revisionStatusLabel(request.status).toLowerCase()}.` };
  }
  if (!isKnownLoopParty(viewerId, facts)) {
    return { allowed: false, reason: `Closing a revision request requires ${REVISION_AUTHORITY}.` };
  }
  return ALLOWED;
}

/**
 * The resolution a close records. The person who asked for the revision
 * taking it back is a withdrawal; anyone else closing it is marking it
 * addressed.
 */
export function closeResolutionFor(
  request: LoopRevisionRequest,
  viewerId: string | null | undefined,
): 'CLOSED' | 'WITHDRAWN' {
  return viewerId && request.requestedBy === viewerId ? 'WITHDRAWN' : 'CLOSED';
}

// ── Involvements ─────────────────────────────────────────────────────────────

/** `{ description }` is what both web (InvolveModal) and iOS write; tolerate a bare string. */
export function involvementText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object') {
    const description = (content as { description?: unknown }).description;
    if (typeof description === 'string') return description;
  }
  return '';
}

/**
 * Involvements the viewer is the target of: the target org unit is one the
 * viewer is placed in. Open ones first, then delivered (so the delivered
 * state stays visible after responding).
 */
export function involvementsForViewer(
  involvements: CatchballTierInvolvement[],
  viewerOrgUnitIds: string[],
): CatchballTierInvolvement[] {
  const mine = new Set(viewerOrgUnitIds);
  return involvements
    .filter((inv) => mine.has(inv.targetOrgUnitId))
    .sort((a, b) => {
      const aOpen = a.state === 'open';
      const bOpen = b.state === 'open';
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      return time(b.createdAt) - time(a.createdAt);
    });
}

/**
 * Only a delegate-mode involvement is delivered explicitly — the server
 * refuses session-mode delivery ("implicitly complete when the session
 * concludes"), so that case is a stated reason, not a button that fails.
 */
export function canDeliverInvolvement(
  involvement: Pick<CatchballTierInvolvement, 'involvementMode' | 'state'>,
  viewerId: string | null | undefined,
): ActionGate {
  if (!viewerId) return SIGN_IN;
  if (involvement.state === 'delivered') {
    return { allowed: false, reason: 'A response has already been delivered.' };
  }
  if (involvement.state !== 'open') {
    return { allowed: false, reason: `This involvement is ${involvement.state}.` };
  }
  if (involvement.involvementMode !== 'delegate') {
    return {
      allowed: false,
      reason: 'You were asked to join a session — that is complete when the session concludes; there is nothing to deliver here.',
    };
  }
  return ALLOWED;
}
